import Button from '@/components/Buttons/Button';
import IconButton from '@/components/Buttons/IconButton';
import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import DeleteModal from '@/components/Modals/DeleteModal';
import { useDeleteVoiceMutation } from '@/hooks/useDeleteVoiceMutation';
import { LocalVoiceFilters, useFilteredVoices, VoiceFiltersEnum } from '@/hooks/useFilteredVoices';
import AltArrowDown from '@/images/arrows/AltArrowDown.svg';
import AltArrowUp from '@/images/arrows/AltArrowUp.svg';
import Close from '@/images/Close.svg';
import { useAnalytics } from '@/services/analytics';
import theme from '@/theme';
import { IVoice } from '@d-id/types';
import styled from '@emotion/styled';
import { DialogContent, Typography } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { ComponentType, ReactNode, useEffect, useRef, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useQueryClient } from 'react-query';
import { GET_VOICES_SEATS_KEY } from '../../../Modals/CloneVoiceWizardModal/useGetVoicesSeats';
import { ELEVEN_LABS_LANGUAGES, ELEVEN_LABS_LOCALES } from '../../voiceInfo';
import { FiltersSection } from './FiltersSection/FiltersSection';
import { ModalViewChange } from './ModalViewChange';
import { VoiceRow } from './VoiceRow/VoiceRow';

const ViewTitle = styled.span`
    color: ${theme.colors.black[900]};
    font-size: 14px;
    font-weight: 600;
`;

const Container = styled.div`
    overflow: hidden;
    max-height: 800px;
    height: 800px;
    padding: 16px 1px 0;
`;

const Header = styled.div`
    display: flex;
    width: 100%;
    align-items: center;
    padding: 0 1rem;
    height: 5rem;
    font-size: 2rem;

    h3 {
        padding: 0 10px;
    }

    svg {
        color: ${theme.colors.black.main};
    }

    button {
        width: 40px;
        height: 40px;
    }

    button:last-of-type {
        margin-left: auto;
    }
`;

const StyledDialogContent = styled(DialogContent)`
    height: calc(100% - 50px);
    overflow: hidden;

    * {
        -ms-overflow-style: none;
        scrollbar-width: none;
    }

    *::-webkit-scrollbar {
        display: none;
    }
`;

const TabContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 8px 0;
`;

const VoicesList = styled.ul`
    overflow: scroll;
    display: flex;
    flex-direction: column;
    gap: 12px;

    .infinite-scroll-component {
        display: flex;
        flex-direction: column;
        gap: 12px;
        padding-bottom: 36px;
    }
`;

export const TransparentButton = styled(Button)<{ disabled?: boolean }>`
    background-color: transparent;
    border: 1px solid ${theme.colors.black[200]};
    height: 34px;
    font-size: 14px;
    padding: 7px 14px;

    ${({ disabled }) =>
        disabled &&
        `
         padding: 7px 14px 7px 30px;
    `}
    &.MuiButtonBase-root:hover {
        background-color: #f3f3f3 !important;
    }

    &.MuiButtonBase-root:disabled {
        background-color: transparent !important;
    }
`;

const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    align-items: center;
    padding-top: 140px;

    h3 {
        margin-bottom: 8px;
    }

    p {
        max-width: 290px;
        line-height: 1.4;
        text-align: center;
    }
`;

const LoaderWrapper = styled.div`
    display: flex;
    justify-content: center;
    padding: 16px 0;
`;

const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

const SectionTitle = styled.span`
    color: ${theme.colors.secondary[700]};
    font-size: 14px;
    font-weight: 500;
`;

const ShowAllButton = styled(Button)<{ white?: boolean }>`
    background-color: ${theme.colors.white};
    color: ${theme.colors.black[700]};
    border: none;

    font-size: 16px;
    font-weight: 600;
    padding: 14px 24px;
    width: min-content;
    white-space: nowrap;
    margin: 0 auto;

    &.MuiButtonBase-root.MuiButton-root.MuiButton-contained:hover {
        background-color: ${theme.colors.black[50]} !important;
    }
`;

export type SubmitFooterType = ComponentType<{ onSubmitted: () => void; selectedVoice: IVoice | null }>;

interface IProps {
    SubmitFooter?: SubmitFooterType;
    onSelectedVoiceChange?: (voice: IVoice) => void;
    selectedVoice: IVoice | null;
    setMode: (mode: 'select' | 'import' | 'clone-voice') => void;
    pinnedContent?: ReactNode;
    filters?: LocalVoiceFilters;
    onClose: () => void;
    hiddenFilters?: VoiceFiltersEnum[];
    showLowLatencyTag?: boolean;
    injectedVoices?: IVoice[];
}

export const SelectVoiceContent = ({
    SubmitFooter,
    onSelectedVoiceChange,
    selectedVoice,
    setMode,
    pinnedContent,
    filters,
    onClose,
    hiddenFilters,
    showLowLatencyTag,
    injectedVoices,
}: IProps) => {
    const queryClient = useQueryClient();
    const mixpanel = useAnalytics();

    const commonVoices = useFilteredVoices({ ...filters, source: 'default' }, injectedVoices);
    const userMultilingualVoices = useFilteredVoices({ ...filters, source: 'imported', language: 'multilingual' });

    const elevenLabsSupported =
        ELEVEN_LABS_LOCALES.includes(filters?.language || '') ||
        ELEVEN_LABS_LANGUAGES.find(lang => lang.toLowerCase() === filters?.language?.toLowerCase());
    const userVoices =
        filters?.language?.toLowerCase() === 'multilingual' ||
        filters?.language?.toLowerCase() === 'all' ||
        // if no language is selected, show all multilingual voices
        !filters?.language ||
        // if language is selected, show all multilingual voices, if language or locale supported
        (filters?.language && elevenLabsSupported)
            ? userMultilingualVoices
            : [];

    const infiniteScrollRef = useRef<any>(null);

    const [collapsedUserVoices, setCollapsedUserVoices] = useState(false);
    const [showAllUserVoices, setShowAllUserVoices] = useState(false);
    const [userItems, setUserItems] = useState<IVoice[]>(userVoices.slice(0, 3));
    const [commonItems, setCommonItems] = useState<IVoice[]>(commonVoices.slice(0, 20));

    const [itemToDelete, setItemToDelete] = useState<IVoice | null>(null);
    const [localSelectedVoice, setLocalSelectedVoice] = useState<IVoice | null>(selectedVoice);
    const { mutateAsync: deleteVoice, isLoading: isDeleting } = useDeleteVoiceMutation();
    const pauseCallbackRef = useRef<VoidFunction | null>(null);

    const isImmediateSelect = !SubmitFooter;
    useEffect(() => {
        setCommonItems(commonVoices.splice(0, 20));
        setUserItems(showAllUserVoices ? userVoices : userVoices.splice(0, 3));

        if (infiniteScrollRef?.current?.el) {
            infiniteScrollRef.current.el.scrollTop = 0;
        }
    }, [commonVoices.length]);

    const loadMoreCommonData = () => {
        setCommonItems(commonItems.concat(commonVoices.splice(commonItems.length, 20)));
    };

    const onCloseDelete = () => setItemToDelete(null);
    const onDelete = async () => {
        if (!itemToDelete) {
            return;
        }
        mixpanel?.track('delete-voice', {
            event: 'click',
            from: 'library',
        });

        await deleteVoice(itemToDelete.id, {
            onSettled: async () => {
                await queryClient.invalidateQueries(GET_VOICES_SEATS_KEY);
                onCloseDelete();
            },
        });
    };

    const handleSelect = (voice: IVoice) => {
        if (isImmediateSelect) {
            onSelectedVoiceChange?.(voice);
            onClose();
        } else {
            setLocalSelectedVoice(voice);
        }
    };

    const handleSubmit = () => {
        onClose();
    };

    const handleClose = () => {
        onClose();
    };

    const handleShowAllUserVoices = () => {
        setUserItems(userVoices);
        setShowAllUserVoices(true);
    };

    const handleCollapseUserVoices = () => {
        setCollapsedUserVoices(s => {
            if (!s) {
                setUserItems(userVoices.slice(0, 3));
                setShowAllUserVoices(false);
            }
            return !s;
        });
    };

    return (
        <>
            <Container>
                <Header>
                    <Typography variant="h3">Select voice</Typography>
                    <IconButton data-testid="voice-modal-close-button" ariaLabel="close" onClick={handleClose}>
                        <MaskedIcon src={Close} size="16px" />
                    </IconButton>
                </Header>

                <StyledDialogContent>
                    <TabContainer>
                        <ViewTitle>Voices library</ViewTitle>
                        <ModalViewChange setMode={setMode} />
                    </TabContainer>
                    <FiltersSection hiddenFilters={hiddenFilters} filters={filters} />

                    {/* Clone voice in here */}
                    {pinnedContent}

                    <VoicesList id="common-voices-container">
                        <InfiniteScroll
                            ref={infiniteScrollRef}
                            scrollableTarget="common-voices-container"
                            dataLength={commonItems.length + userItems.length}
                            next={loadMoreCommonData}
                            hasMore={commonVoices.length > commonItems.length}
                            loader={
                                <LoaderWrapper>
                                    <CircularProgress />
                                </LoaderWrapper>
                            }>
                            {!!userVoices.length && (
                                <>
                                    <SectionHeader>
                                        <SectionTitle>User voices</SectionTitle>
                                        <IconButton
                                            onClick={handleCollapseUserVoices}
                                            ariaLabel={collapsedUserVoices ? 'Collapse' : 'Expand'}>
                                            <MaskedIcon src={collapsedUserVoices ? AltArrowDown : AltArrowUp} size="24px" />
                                        </IconButton>
                                    </SectionHeader>
                                    {!collapsedUserVoices && (
                                        <>
                                            {userItems.map(voice => (
                                                <VoiceRow
                                                    personal
                                                    pauseCallbackRef={pauseCallbackRef}
                                                    key={`${voice.id}-${voice.language}`}
                                                    voice={voice}
                                                    onDelete={() => setItemToDelete(voice)}
                                                    onSelect={() => handleSelect(voice)}
                                                    isSelected={
                                                        isImmediateSelect
                                                            ? selectedVoice?.id === voice.id && selectedVoice?.language === voice.language
                                                            : localSelectedVoice?.id === voice.id &&
                                                              localSelectedVoice?.language === voice.language
                                                    }
                                                />
                                            ))}
                                            {userVoices.length > 3 && !showAllUserVoices && (
                                                <ShowAllButton onClick={handleShowAllUserVoices}>Show all</ShowAllButton>
                                            )}
                                        </>
                                    )}
                                </>
                            )}
                            {!!commonVoices.length && (
                                <>
                                    <SectionHeader>
                                        <SectionTitle>Voice library</SectionTitle>
                                    </SectionHeader>
                                    {commonItems.map(voice => (
                                        <VoiceRow
                                            showLowLatencyTag={showLowLatencyTag}
                                            pauseCallbackRef={pauseCallbackRef}
                                            key={`${voice.id}-${voice.language}`}
                                            voice={voice}
                                            onDelete={() => setItemToDelete(voice)}
                                            onSelect={() => handleSelect(voice)}
                                            isSelected={
                                                isImmediateSelect
                                                    ? selectedVoice?.id === voice.id && selectedVoice?.language === voice.language
                                                    : localSelectedVoice?.id === voice.id && localSelectedVoice?.language === voice.language
                                            }
                                            isExpressiveVoice={injectedVoices?.some(v => v.id === voice.id)}
                                        />
                                    ))}
                                </>
                            )}
                        </InfiniteScroll>
                    </VoicesList>

                    {!userVoices.length && !commonVoices.length && (
                        <EmptyState>
                            <Typography variant="h3">No voices found</Typography>
                            <Typography variant="body1" color="textSecondary">
                                We couldn’t find any voices matching your search. Try another one
                            </Typography>
                        </EmptyState>
                    )}
                </StyledDialogContent>

                <DeleteModal
                    itemType="voice"
                    mode="square"
                    open={!!itemToDelete}
                    isLoading={isDeleting}
                    onDelete={onDelete}
                    onClose={onCloseDelete}
                    text={{
                        title: 'Confirm delete',
                        content: `Are you sure you want to delete this voice from your library?`,
                        cta: 'Delete voice',
                    }}
                />
            </Container>
            {SubmitFooter && <SubmitFooter onSubmitted={handleSubmit} selectedVoice={localSelectedVoice} />}
        </>
    );
};
